import React from "react";
import { ShieldCheck, RefreshCcw, CreditCard, Cookie, ArrowRight, Clock, ShieldAlert } from "lucide-react";
import { Link } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import SEO from "../components/SEO";

const sections = [
  {
    num: "01",
    icon: <ShieldCheck size={22} />,
    title: "Return Eligibility",
    text: "Thrift and Archive pieces are limited edition items. We only accept returns if the product does not match the description, a wrong item is delivered, or it is damaged during transit. All return requests must be raised within 48 hours of delivery."
  },
  {
    num: "02",
    icon: <RefreshCcw size={22} />,
    title: "How to Return",
    text: "To initiate a return, contact our support team via email or chat. You must fill out an official Return Request Form and attach product images, a copy of the invoice, and a mandatory unboxing video."
  },
  {
    num: "03",
    icon: <CreditCard size={22} />,
    title: "Refund Process",
    text: "Once the item is inspected at our warehouse, a refund is initiated within 48 hours. Refunds are processed only to the original payment method. Bank processing time may take 3–7 working days."
  },
  {
    num: "04",
    icon: <Cookie size={22} />, 
    title: "Cookie Protocol",
    text: "We use cookies solely for essential purposes such as managing cart data and login sessions. No personal data is shared with third parties. Our encryption system keeps your data 100% secure."
  },
  {
    num: "05",
    icon: <Clock size={22} />, 
    title: "Asset Validity",
    text: "Wallet tokens and referral rewards are active for 365 days from your last successful order. To prevent asset decommissioning (wallet reset to 0), ensure at least one transaction is finalized within the 1-year cycle."
  }
];

export default function Returns() {
  const { isDarkMode } = useTheme();

  return (
    <>
      <SEO 
        title="Returns & Cookie Protocol | Archive Security" 
        description="Official return policy and data protocols for THE_ARCHIVE. Secure transactions and transparent asset management."
        url="/returns"
      />

      {/* Main Container - Pure Black/White Sync */}
      <section className={`min-h-screen transition-colors duration-300 py-32 px-6 md:px-12 ${isDarkMode ? 'bg-[#050505] text-white' : 'bg-white text-black'}`}>
        <div className="max-w-6xl mx-auto">

          {/* Header Chassis */}
          <div className="mb-24 text-center">
             <div className="flex items-center justify-center gap-3 mb-6">
               <div className="w-8 h-[2px] bg-emerald-500"></div>
               <p className="text-[10px] font-black uppercase tracking-[0.5em] text-emerald-500">Security_Protocol_v1.2</p>
               <div className="w-8 h-[2px] bg-emerald-500"></div>
            </div>
            <h1 className="text-5xl md:text-8xl font-black tracking-tighter uppercase italic leading-none">
              RETURNS <span className="opacity-20">&</span> COOKIES
            </h1>
            <p className="mt-6 text-[11px] font-bold uppercase tracking-[0.3em] opacity-40">
              Protocol Synchronized · 18 FEB 2026
            </p>
          </div>

          {/* Sections Grid - Border Only Style */}
          <div className="grid md:grid-cols-2 gap-8">
            {sections.map((item, i) => (
              <div
                key={i}
                className={`relative border-2 rounded-[2.5rem] p-10 transition-all duration-500 group bg-transparent ${
                  isDarkMode 
                  ? 'border-white/5 hover:border-emerald-500/50' 
                  : 'border-black/5 hover:border-emerald-500/50'
                }`}
              >
                {/* Number Badge - Inverted Pure Colors */}
                <div className={`absolute -top-4 -left-4 w-12 h-12 rounded-2xl flex items-center justify-center font-black text-sm border-2 transition-all group-hover:bg-emerald-500 group-hover:border-emerald-500 group-hover:text-black ${
                  isDarkMode ? 'bg-black border-white/20 text-white' : 'bg-white border-black/20 text-black'
                }`}>
                  {item.num}
                </div>

                {/* Icon Accent */}
                <div className={`mb-6 transition-colors group-hover:text-emerald-500 opacity-40 group-hover:opacity-100`}>
                  {item.icon}
                </div>

                {/* Title Chassis */}
                <h3 className="text-2xl font-black italic uppercase tracking-tighter mb-4 transition-colors group-hover:text-emerald-500">
                  {item.title}
                </h3>

                {/* Body Text */}
                <p className={`text-[12px] leading-relaxed font-bold uppercase tracking-wide opacity-50`}>
                  {item.text}
                </p>
              </div>
            ))}
          </div>

          {/* New Policy Highlight: Trust & Wallet Security */}
          <div className={`mt-12 p-8 rounded-[2rem] border-2 border-dashed flex flex-col md:flex-row items-center gap-6 ${isDarkMode ? 'border-white/10 bg-white/5' : 'border-black/5 bg-black/5'}`}>
             <div className="p-4 rounded-full bg-emerald-500/10 border border-emerald-500/20">
                <ShieldAlert className="text-emerald-500" size={30} />
             </div>
             <div>
                <h4 className="text-[10px] font-black uppercase tracking-widest text-emerald-500 mb-2">Wallet_Integrity_Report</h4>
                <p className="text-[11px] font-bold uppercase opacity-60 leading-relaxed">
                   To ensure network stability, all referral credits and wallet balances must remain active via engagement. 
                   A period of 365 days of inactivity results in an automated vault purge. Stay active to protect your digital assets.
                </p>
             </div>
          </div>

          {/* Final CTA Portal - Pure High-Contrast */}
          <div className={`relative overflow-hidden mt-20 p-10 md:p-20 rounded-[3rem] border-2 transition-all ${
            isDarkMode 
            ? 'border-white/10' 
            : 'border-black/5 shadow-2xl shadow-black/5'
          }`}>
            
            {/* Signature Emerald Glow */}
            <div className={`absolute -bottom-20 -right-20 w-64 h-64 blur-[120px] rounded-full ${isDarkMode ? 'bg-emerald-500/10' : 'bg-emerald-500/5'}`} />

            <div className="relative z-10 max-w-3xl">
              <h4 className="text-emerald-500 text-[10px] font-black uppercase tracking-[0.5em] mb-8">
                Official_Verification_Portal
              </h4>

              <p className="text-xl md:text-3xl leading-tight mb-12 font-black italic uppercase tracking-tighter">
                Need to initiate a return? Our archive experts are ready to verify your claim.
              </p>

              <Link to="/contact">
                <button className={`group inline-flex items-center gap-6 px-12 py-6 rounded-2xl font-black text-[11px] uppercase tracking-[0.3em] transition-all active:scale-95 shadow-xl ${
                  isDarkMode 
                  ? 'bg-white text-black hover:bg-emerald-500 hover:text-white' 
                  : 'bg-black text-white hover:bg-emerald-500'
                }`}>
                  Open_Return_Ticket
                  <ArrowRight size={18} className="group-hover:translate-x-2 transition-transform" />
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}